import { useMemo, useState } from 'react';
import { differenceInDays, format, parseISO, startOfDay } from 'date-fns';
import { CalendarClock, Plus } from 'lucide-react';
import { toast } from 'sonner';
import AppDateField from '@/components/AppDateField';
import type { Child, FamilyAdminItem, FamilyAdminStatus, FamilySchoolDeadlineKind } from '@/types';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppEmptyState,
  AppFieldGroup,
  AppInlineAlert,
  AppInput,
  AppLoadingState,
  AppSectionHeader,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import { AppNativeSelect } from '../../../ui-foundation/adapters/web';

const STATUS_CLASSES: Record<FamilyAdminStatus, string> = {
  open: 'bg-mustard-light text-accent',
  waiting: 'bg-plum-light text-primary',
  done: 'bg-success-light text-success',
};

const DEADLINE_OPTIONS: Array<{
  value: FamilySchoolDeadlineKind;
  label: string;
  leadDays: number;
  description: string;
}> = [
  {
    value: 'form',
    label: 'School form deadline',
    leadDays: 14,
    description: 'Forms, payments, or confirmations that need sending back.',
  },
  {
    value: 'parents-evening',
    label: "Parents' evening",
    leadDays: 21,
    description: 'Keep booking dates and attendance in one place.',
  },
  {
    value: 'trip-permission',
    label: 'Trip permission',
    leadDays: 14,
    description: 'Catch consent slips, payment deadlines, and pickup notes.',
  },
  {
    value: 'uniform-list',
    label: 'Uniform reminder',
    leadDays: 30,
    description: 'Use this for term starts, replacement checks, or PE kit reminders.',
  },
  {
    value: 'school-place-reception',
    label: 'Reception place application',
    leadDays: 56,
    description: 'Adulting flags this early because missing the window causes real stress.',
  },
  {
    value: 'school-place-secondary',
    label: 'Secondary place application',
    leadDays: 56,
    description: 'Keep the autumn deadline visible well before it turns urgent.',
  },
];

const GENERIC_SCHOOL_META = {
  value: 'general' as FamilySchoolDeadlineKind,
  label: 'School admin',
  leadDays: 14,
  description: 'Use this for general school admin that still needs a due date and a reminder window.',
};

function formatStatus(status: FamilyAdminStatus) {
  if (status === 'waiting') return 'Waiting';
  if (status === 'done') return 'Done';
  return 'Open';
}

function getDeadlineMeta(kind: FamilySchoolDeadlineKind | undefined) {
  if (!kind || kind === 'general') return GENERIC_SCHOOL_META;
  return DEADLINE_OPTIONS.find((option) => option.value === kind) || GENERIC_SCHOOL_META;
}

function getSchoolDeadlineTitle(kind: FamilySchoolDeadlineKind, childName: string) {
  const childPrefix = childName.trim() ? `${childName.trim()}: ` : '';
  switch (kind) {
    case 'parents-evening':
      return `${childPrefix}Parents' evening`;
    case 'trip-permission':
      return `${childPrefix}Return school trip permission`;
    case 'uniform-list':
      return `${childPrefix}Uniform reminder`;
    case 'school-place-reception':
      return `${childPrefix}Reception place application`;
    case 'school-place-secondary':
      return `${childPrefix}Secondary school place application`;
    case 'form':
    case 'general':
    default:
      return `${childPrefix}School form deadline`;
  }
}

function nextAnnualDate(monthIndex: number, dayOfMonth: number) {
  const today = startOfDay(new Date());
  const currentYear = today.getFullYear();
  const candidate = new Date(currentYear, monthIndex, dayOfMonth);
  const nextDate = candidate >= today ? candidate : new Date(currentYear + 1, monthIndex, dayOfMonth);
  return format(nextDate, 'yyyy-MM-dd');
}

function defaultDueDate(kind: FamilySchoolDeadlineKind) {
  if (kind === 'school-place-reception') return nextAnnualDate(0, 15);
  if (kind === 'school-place-secondary') return nextAnnualDate(9, 31);
  return '';
}

function isInsideReminderWindow(item: FamilyAdminItem) {
  if (!item.dueDate) return false;
  const leadDays = item.reminderDaysBefore ?? 14;
  const daysUntil = differenceInDays(parseISO(item.dueDate), startOfDay(new Date()));
  return daysUntil <= leadDays;
}

type SaveInput = {
  childName: string;
  dueDate: string;
  notes: string;
  deadlineKind: FamilySchoolDeadlineKind;
  reminderDaysBefore: number;
  title: string;
};

export default function FamilySchoolDeadlineSection({
  children,
  items,
  onSaveDeadline,
  onUpdateStatus,
}: {
  children: Child[];
  items: FamilyAdminItem[];
  onSaveDeadline: (input: SaveInput) => Promise<void>;
  onUpdateStatus: (itemId: string, status: FamilyAdminStatus) => Promise<void>;
}) {
  const namedChildren = useMemo(
    () => children.filter((child) => child.name.trim().length > 0),
    [children],
  );
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [childName, setChildName] = useState(namedChildren[0]?.name || '');
  const [deadlineKind, setDeadlineKind] = useState<FamilySchoolDeadlineKind>('form');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');

  const deadlineItems = useMemo(
    () => items
      .filter((item) => item.area === 'school' && item.dueDate)
      .sort((left, right) => String(left.dueDate).localeCompare(String(right.dueDate))),
    [items],
  );

  const stats = useMemo(() => ({
    tracked: deadlineItems.length,
    alertWindow: deadlineItems.filter((item) => item.status !== 'done' && isInsideReminderWindow(item)).length,
  }), [deadlineItems]);

  const receptionCandidates = useMemo(
    () => namedChildren.filter((child) => child.age >= 3 && child.age <= 4),
    [namedChildren],
  );
  const secondaryCandidates = useMemo(
    () => namedChildren.filter((child) => child.age >= 10 && child.age <= 11),
    [namedChildren],
  );

  const openForm = (kind: FamilySchoolDeadlineKind = 'form', nextChildName = namedChildren[0]?.name || '') => {
    setChildName(nextChildName);
    setDeadlineKind(kind);
    setDueDate(defaultDueDate(kind));
    setNotes('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!childName.trim() || !dueDate) return;
    const meta = getDeadlineMeta(deadlineKind);
    setSaving(true);

    try {
      await onSaveDeadline({
        childName: childName.trim(),
        dueDate,
        notes: notes.trim(),
        deadlineKind,
        reminderDaysBefore: meta.leadDays,
        title: getSchoolDeadlineTitle(deadlineKind, childName),
      });
      toast.success('School deadline saved.');
      setSaving(false);
      setShowForm(false);
      setNotes('');
      setDueDate('');
    } catch (error) {
      console.error(error);
      toast.error('Could not save that school deadline right now.');
      setSaving(false);
    }
  };

  const addSuggestedDeadline = async (kind: FamilySchoolDeadlineKind, nextChildName: string) => {
    const meta = getDeadlineMeta(kind);
    try {
      await onSaveDeadline({
        childName: nextChildName,
        dueDate: defaultDueDate(kind),
        notes: 'Double-check the exact local admissions date if your council or school uses a different cutoff.',
        deadlineKind: kind,
        reminderDaysBefore: meta.leadDays,
        title: getSchoolDeadlineTitle(kind, nextChildName),
      });
      toast.success('Suggested school deadline added.');
    } catch (error) {
      console.error(error);
      toast.error('Could not add that suggested school deadline.');
    }
  };

  return (
    <AppCard>
      <AppSectionHeader
        title="School deadline tracker"
        subtitle="Keep forms, school-place windows, trips, and parents' evening deadlines visible with an alert window that matches the type of admin."
        action={(
          <AppButton
            size="sm"
            onPress={() => (showForm ? setShowForm(false) : openForm())}
            disabled={namedChildren.length === 0}
          >
            <Plus size={12} />
            {showForm ? 'Cancel' : 'Add'}
          </AppButton>
        )}
      />

      <div className="grid grid-cols-2 gap-2 mt-4 sm:grid-cols-4">
        <MiniMetric label="Tracked" value={stats.tracked} />
        <MiniMetric label="Alerting" value={stats.alertWindow} />
      </div>

      {receptionCandidates.length > 0 || secondaryCandidates.length > 0 ? (
        <AppCard backgroundColor="$background" className="mt-4">
          <AppText fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">
            School place quick starts
          </AppText>
          <AppButtonRow mt="$sm">
            {receptionCandidates.map((child) => (
              <AppButton
                key={`reception-${child.id}`}
                size="sm"
                tone="outline"
                onPress={() => void addSuggestedDeadline('school-place-reception', child.name)}
              >
                Add {child.name} reception deadline
              </AppButton>
            ))}
            {secondaryCandidates.map((child) => (
              <AppButton
                key={`secondary-${child.id}`}
                size="sm"
                tone="outline"
                onPress={() => void addSuggestedDeadline('school-place-secondary', child.name)}
              >
                Add {child.name} secondary deadline
              </AppButton>
            ))}
          </AppButtonRow>
        </AppCard>
      ) : null}

      {showForm ? (
        <AppCard backgroundColor="$mutedBackground" className="mt-4 animate-fade-in">
          {namedChildren.length === 0 ? (
            <AppInlineAlert title="Add a child in Profile first">
              Then save school-admin deadlines here.
            </AppInlineAlert>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AppFieldGroup label="Child">
                  <AppNativeSelect
                    value={childName}
                    onChange={(event) => setChildName(event.target.value)}
                  >
                    {namedChildren.map((child) => (
                      <option key={child.id} value={child.name}>
                        {child.name}
                      </option>
                    ))}
                  </AppNativeSelect>
                </AppFieldGroup>
                <AppFieldGroup label="Deadline type">
                  <AppNativeSelect
                    value={deadlineKind}
                    onChange={(event) => {
                      const nextKind = event.target.value as FamilySchoolDeadlineKind;
                      setDeadlineKind(nextKind);
                      if (!dueDate || deadlineKind === 'school-place-reception' || deadlineKind === 'school-place-secondary') {
                        setDueDate(defaultDueDate(nextKind));
                      }
                    }}
                  >
                    {DEADLINE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </AppNativeSelect>
                </AppFieldGroup>
              </div>

              <AppDateField label="Due date" value={dueDate} onChange={setDueDate} />

              <AppCard backgroundColor="$background">
                <AppText fontSize="$xs" color="$colorMuted">
                  {getDeadlineMeta(deadlineKind).description} Adulting will start flagging it {getDeadlineMeta(deadlineKind).leadDays} days before it is due.
                </AppText>
              </AppCard>

              <AppTextArea
                value={notes}
                onChange={(event) => setNotes((event.target as HTMLTextAreaElement).value)}
                rows={3}
                placeholder="Anything practical you need to remember: link code, teacher, payment amount, or what has to be sent back."
              />

              <AppButton
                onPress={() => void handleSave()}
                disabled={!childName.trim() || !dueDate || saving}
              >
                {saving ? 'Saving…' : 'Save school deadline'}
              </AppButton>
            </>
          )}
        </AppCard>
      ) : null}

      <div className="space-y-3 mt-4">
        {deadlineItems.length === 0 ? (
          <AppEmptyState
            title="No structured school deadlines yet"
            body="Save the next form, trip permission, school-place date, or parents' evening here so Adulting can start warning you in time."
          />
        ) : (
          deadlineItems.map((item) => {
            const meta = getDeadlineMeta(item.deadlineKind);
            const daysUntil = item.dueDate ? differenceInDays(parseISO(item.dueDate), startOfDay(new Date())) : null;
            return (
              <AppCard key={item.id} backgroundColor="$background">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <AppText fontSize="$sm" fontWeight="700">{item.title}</AppText>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_CLASSES[item.status]}`}>
                        {formatStatus(item.status)}
                      </span>
                    </div>
                    <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                      {item.childName || 'General'}
                      {item.dueDate ? ` · ${format(parseISO(item.dueDate), 'd MMM yyyy')}` : ''}
                      {' · '}
                      {meta.label}
                    </AppText>
                    <div className="space-y-1 mt-2">
                      <AppText fontSize="$sm" color="$colorMuted">
                        {daysUntil == null
                          ? 'No due date saved yet.'
                          : daysUntil < 0
                            ? `Overdue by ${Math.abs(daysUntil)} day${Math.abs(daysUntil) === 1 ? '' : 's'}`
                            : `Alert window starts ${meta.leadDays} days early${daysUntil <= meta.leadDays ? ' and this is already inside it' : ''}.`}
                      </AppText>
                      {item.notes ? <AppText fontSize="$sm" color="$colorMuted">{item.notes}</AppText> : null}
                    </div>
                  </div>
                  <CalendarClock size={16} className="shrink-0 text-muted-foreground mt-1" />
                </div>
                <AppButtonRow mt="$sm">
                  {item.status !== 'waiting' && item.status !== 'done' ? (
                    <AppButton
                      size="sm"
                      tone="outline"
                      onPress={() => void onUpdateStatus(item.id, 'waiting')}
                    >
                      Mark waiting
                    </AppButton>
                  ) : null}
                  {item.status !== 'done' ? (
                    <AppButton
                      size="sm"
                      tone="outline"
                      onPress={() => void onUpdateStatus(item.id, 'done')}
                    >
                      Mark done
                    </AppButton>
                  ) : (
                    <AppButton
                      size="sm"
                      tone="outline"
                      onPress={() => void onUpdateStatus(item.id, 'open')}
                    >
                      Reopen
                    </AppButton>
                  )}
                </AppButtonRow>
              </AppCard>
            );
          })
        )}
      </div>
    </AppCard>
  );
}

function MiniMetric({ label, value }: { label: string; value: number }) {
  return (
    <AppCard px="$sm" py="$sm" backgroundColor="$background">
      <AppText fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">
        {label}
      </AppText>
      <AppText className="mt-1" fontSize="$xl" fontWeight="700">
        {value}
      </AppText>
    </AppCard>
  );
}
